if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButton.addEventListener('click', this.onSubmitButtonClick, true);
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getProductAddOns() {
        const productInfo = this.closest('product-info');
        const scoped = productInfo ? productInfo.querySelector('product-add-ons') : null;
        if (scoped) return scoped;

        // The theme renders buy-buttons a second time inside the whole-page
        // sticky bar, which sits in the media column outside <product-info>.
        // Without this fallback that button would submit the main product on
        // its own and silently drop every selected add-on.
        // Match on the section id rather than building an attribute selector,
        // so no escaping of the id is needed.
        const sectionWrapper = this.closest('[id^="shopify-section-"]');
        const all = Array.from(document.querySelectorAll('product-add-ons'));
        if (sectionWrapper) {
          const sectionId = sectionWrapper.id.replace('shopify-section-', '');
          const bySection = all.find((element) => element.dataset.sectionId === sectionId);
          if (bySection) return bySection;
        }

        return all.length === 1 ? all[0] : null;
      }

      onSubmitButtonClick(event) {
        const addOnItems = this.getSelectedAddOnItems(this.getProductAddOns());
        if (addOnItems.length === 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.onSubmitHandler(event);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const addOns = this.getProductAddOns();
        const addOnItems = this.getSelectedAddOnItems(addOns);

        if (addOnItems.length > 0) {
          const quantityInput = this.form.querySelector('[name="quantity"]');
          const mainQuantity = quantityInput ? Math.max(1, Number.parseInt(quantityInput.value, 10) || 1) : 1;
          const payload = {
            items: this.buildCartItems(formData, mainQuantity, addOnItems),
          };

          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(this.normalizeCartResponse(response));
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(this.normalizeCartResponse(response));
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      normalizeCartResponse(response) {
        if (!response || !Array.isArray(response.items) || response.items.length === 0) return response;

        return {
          ...response.items[0],
          sections: response.sections,
        };
      }

      getSelectedAddOnItems(addOns) {
        if (!addOns) return [];
        if (typeof addOns.getSelectedItems === 'function') {
          const selectedItems = addOns.getSelectedItems();
          return Array.isArray(selectedItems) ? selectedItems.slice(0, 5) : [];
        }

        return Array.from(addOns.querySelectorAll('[data-addon-checkbox]:checked:not(:disabled)'))
          .slice(0, 5)
          .map((checkbox) => ({
            id: Number.parseInt(checkbox.dataset.variantId || checkbox.value, 10),
            quantity: 1,
          }))
          .filter((item) => Number.isFinite(item.id) && item.id > 0);
      }

      buildCartItems(formData, quantity, addOnItems) {
        return [this.buildMainCartItem(formData, quantity), ...addOnItems.slice(0, 5)];
      }

      buildMainCartItem(formData, quantity) {
        const item = {
          id: Number.parseInt(formData.get('id'), 10),
          quantity,
        };
        const sellingPlan = formData.get('selling_plan');
        const properties = {};

        if (sellingPlan) item.selling_plan = Number.parseInt(sellingPlan, 10);

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch && typeof value === 'string' && value !== '') {
            properties[propertyMatch[1]] = value;
          }
        });

        if (Object.keys(properties).length > 0) item.properties = properties;
        return item;
      }
    }
  );
}
